import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type { UpdateIndicatorMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateIndicatorDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { FrontendRouterInputs } from '../routerInputs';
import type { UpdateIndicatorInput } from '../scheduledEntityInputs';

type UpdateIndicatorTRPCInput = FrontendRouterInputs['indicator']['update'];

export const useUpdateIndicator = createMutationHook({
  name: 'updateIndicator',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.indicator.update.mutationOptions({ onSuccess }),
  mapInput: ({
    schedule,
    Title,
    Type,
    ...rest
  }: UpdateIndicatorInput): UpdateIndicatorTRPCInput => {
    if (!Title || !Type) {
      throw new Error('Title and Type are required for indicator update');
    }

    return {
      ...rest,
      Title,
      Type,
      ...mapScheduleToTRPC(schedule),
    };
  },
  invalidate: (trpc) => [
    trpc.frontend.indicator.register.queryKey(),
    trpc.frontend.indicator.indicatorById.queryKey(),
    trpc.frontend.indicator.indicatorsByParentId.queryKey(),
    trpc.frontend.indicator.indicatorResultsByIndicatorId.queryKey(),
    // COI-889: the risk and control registers' Associations column carries the
    // indicator title and count, as `useInsertIndicator` already declares.
    trpc.frontend.control.register.queryKey(),
    trpc.frontend.risk.register.queryKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  mapResponse: (result: { Id: string }): UpdateIndicatorMutation => ({
    updateChildIndicator: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: UpdateIndicatorDocument,
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...input
  }: UpdateIndicatorInput) => ({ object: input }),
  evictFields: ['indicator'],
});
